<template>
  <main class="relative">
    <img
      src="/images/backgrounds/contact-bg.png"
      ref="contactBg"
      class="absolute top-[50vh] left-0 -z-10 h-full w-full select-none object-contain"
      alt="background"
    />
    <GridsOutterGrid>
      <SectionSet is-first class="col-span-1 col-start-2">
        <GridsTwelveGrid>
          <BreadCrumbs
            class="col-span-6 col-start-1 flex capitalize laptop:col-span-1 laptop:col-start-11 laptop:justify-end laptop:text-right"
          />
          <h1 class="col-span-6 col-start-1 capitalize laptop:col-span-7 laptop:col-start-5 laptop:text-right">
            {{ data?.contactPage?.data?.attributes?.hero_title }}
          </h1>
          <div
            class="col-span-full col-start-1 mt-12 italic laptop:col-span-7 laptop:col-start-2 laptop:mt-16"
            v-html="marked.parse(data?.contactPage?.data?.attributes?.hero_paragraph_1 || '')"
          />

          <div class="col-span-full col-start-1 mt-12 italic laptop:col-span-7 laptop:col-start-2 laptop:mt-16">
            {{ $t("you-can") }}

            <HighlightedLink :to="{ name: 'for-business' }" class="link">
              <span class="underlined relative leading-[1.2]">{{ $t("customize-your-products") }}</span>
            </HighlightedLink>
            {{ $t("get-back-to-you") }}
          </div>

          <div
            class="col-span-full col-start-1 mt-12 flex space-x-4 rounded-3xl bg-[#E7F4EB4D] bg-opacity-30 p-4 laptop:col-span-6 laptop:col-start-6 laptop:mt-14"
          >
            <div class="grid w-full grid-cols-6 gap-x-6 laptop:mr-6 laptop:grid-cols-6">
              <div class="col-span-6 col-start-1 flex laptop:col-span-3">
                <Icon name="fluent:call-24-regular" size="28" class="mr-4 text-ng300" />
                <ul class="font-sans text-ng800">
                  <li
                    v-for="telephone in data?.contactPage?.data?.attributes?.telephones"
                    :key="telephone?.text || JSON.stringify(telephone)"
                  >
                    <a :href="`tel:${telephone?.text}`">
                      {{ telephone?.text }}
                    </a>
                  </li>
                </ul>
              </div>
              <div class="col-span-6 col-start-1 mt-6 flex laptop:col-span-3 laptop:mt-0">
                <Icon name="fluent:mail-16-regular" size="28" class="mr-4 text-ng300" />
                <ul class="space-y-4 font-sans text-ng800">
                  <li
                    v-for="address in data?.contactPage?.data?.attributes?.contact_emails"
                    :key="address?.department_email || JSON.stringify(address)"
                  >
                    <div class="text-ng300">{{ address?.department_name }}</div>
                    <a :href="`mailto:${address?.department_email}`">
                      {{ address?.department_email }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <ContactContactForm
            class="col-span-full col-start-1 mt-12 laptop:col-span-12 laptop:col-start-1 laptop:mt-16"
          />
        </GridsTwelveGrid>
      </SectionSet>
      <SectionSet class="col-span-1 col-start-2">
        <GridsTwelveGrid>
          <h2 class="col-span-full col-start-1 laptop:col-span-6 laptop:col-start-3">
            {{ data?.contactPage?.data?.attributes?.visit_title_1 }}
          </h2>
          <p
            class="col-span-full col-start-1 pt-8 laptop:col-span-6 laptop:col-start-5 laptop:pt-10"
            v-html="data?.contactPage?.data?.attributes?.visit_paragraph_1"
          />
          <div class="col-span-full col-start-1 flex pt-8 laptop:col-span-6 laptop:col-start-5 laptop:pt-6">
            <Icon class="mr-4 shrink-0 text-ng300" name="ph:map-pin" size="24" />
            <div>
              <div class="text-ng600">
                {{ $t("our-address") }}
              </div>
              <div>
                <NuxtLink
                  target="_blank"
                  class="hover:text-ng800"
                  :to="`${data?.contactPage?.data?.attributes?.visit_address_link}`"
                >
                  {{ data?.contactPage?.data?.attributes?.visit_address }}
                </NuxtLink>
              </div>
            </div>
          </div>
        </GridsTwelveGrid>
      </SectionSet>
    </GridsOutterGrid>
    <SectionSet class="relative mt-8">
      <GridsOutterGrid>
        <GridsTwelveGrid class="col-span-1 col-start-2 row-start-1">
          <div
            class="col-span-6 col-start-1 mt-8 h-auto max-h-[528px] w-full laptop:col-span-6 laptop:col-start-3"
            ref="sateliteImage"
          >
            <div class="h-full w-full">
              <iframe
                width="600"
                height="450"
                style="border: 0"
                class="h-full w-full"
                loading="lazy"
                allowfullscreen
                referrerpolicy="no-referrer-when-downgrade"
                :src="data?.contactPage?.data?.attributes?.gmaps_src"
              >
              </iframe>
            </div>
          </div>
        </GridsTwelveGrid>
        <ImageStrapi
          height="full"
          fit="cover"
          width="full"
          class="-z-10 col-span-3 col-start-1 row-start-1 mt-25 h-full min-h-[242px] w-full object-cover object-center laptop:mt-36"
          :img="data?.contactPage?.data?.attributes?.footer_image_1"
          alt="sateline image"
        />
      </GridsOutterGrid>
    </SectionSet>
  </main>
</template>
<script setup lang="ts">
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useWaitForPageTransitionEnd } from "~~/composables/waitPageTransition";
import { marked } from "marked";

const contactBg = ref<HTMLImageElement | null>(null);

const pageTitle = ref<string | null>(null);

const sateliteImage = ref<HTMLDivElement | null>(null);

const { locale, finalizePendingLocaleChange } = useI18n();

await finalizePendingLocaleChange();

const { data } = await useAsyncGql({
  operation: "contactPage",
  variables: { locale: locale.value },
  options: {
    lazy: true,
  },
});

watch(
  data,
  (newVal) => {
    if (newVal) {
      if (newVal.contactPage?.data?.attributes?.seo?.metaTitle) {
        pageTitle.value = newVal.contactPage?.data?.attributes?.seo?.metaTitle;
      }
      useHead({
        title: () => pageTitle.value,
      });
      useServerSeoMeta({
        title: () => `${newVal.contactPage?.data?.attributes?.seo?.metaTitle}`,
        ogTitle: () => `${newVal.contactPage?.data?.attributes?.seo?.metaTitle}`,
        description: () => `${newVal.contactPage?.data?.attributes?.seo?.metaDescription}`,
        ogDescription: () => `${newVal.contactPage?.data?.attributes?.seo?.metaDescription}`,
        ogImage: () => `${newVal.contactPage?.data?.attributes?.seo?.metaImage?.data?.attributes?.url}`,
      });
    }
  },
  { immediate: true },
);

const { promise } = useWaitForPageTransitionEnd();

onMounted(() => {
  initAnimation();
});
const tweens: Array<GSAPTween> = [];
async function initAnimation() {
  await promise.value;

  if (contactBg.value) {
    tweens.push(
      gsap.to(contactBg.value, {
        scrollTrigger: {
          trigger: contactBg.value,
          scrub: 1,
          start: "top top",
          end: "center center",
        },
        yPercent: -15,
      }),
    );
  }

  if (sateliteImage.value) {
    tweens.push(
      gsap.to(sateliteImage.value, {
        scrollTrigger: {
          trigger: sateliteImage.value,
          scrub: 1,
          start: "center 80%",
          end: "center center",
        },
        yPercent: -5,
        scale: 1.05,
      }),
    );
  }
}

onBeforeUnmount(() => {
  tweens.forEach((tween) => tween.kill());
  tweens.length = 0;
});
</script>
<style lang="scss" scoped></style>
